/**
 * Error Alert Deduplication Utility
 *
 * Tracks which errors (by fingerprint) have been alerted in the current session
 * to prevent alert fatigue and ensure one alert per unique error per session.
 *
 * Features:
 * - Session-based tracking: Alerts tied to the shared browser session
 * - Automatic cleanup: Limits to 100 entries, removes oldest
 * - Session change detection: Clears old session alerts
 * - Quota handling: Gracefully handles sessionStorage quota exceeded
 *
 * Storage: sessionStorage key 'sw_alerted_errors'
 * Structure: { [fingerprint]: { fingerprint, timestamp, session_id } }
 */

import { getSessionId } from './tracking-context';

interface AlertedError {
  fingerprint: string;
  timestamp: number;
  session_id: string;
}

class ErrorAlertDeduplicator {
  private readonly STORAGE_KEY = 'sw_alerted_errors';
  private readonly MAX_ENTRIES = 100;

  /**
   * Check if this error has been alerted in current session
   * @param fingerprint - Error fingerprint (error_type:message:file:line)
   * @returns true if error was already alerted in this session
   */
  hasBeenAlerted(fingerprint: string): boolean {
    const sessionId = this.getCurrentSessionId();
    if (!sessionId) return false;

    const alerted = this.getAlertedErrors();
    const entry = alerted[fingerprint];

    // If alerted in current session, skip
    if (entry && entry.session_id === sessionId) {
      return true;
    }

    return false;
  }

  /**
   * Mark error as alerted for current session
   * @param fingerprint - Error fingerprint to mark as alerted
   */
  markAsAlerted(fingerprint: string): void {
    const sessionId = this.getCurrentSessionId();
    if (!sessionId) return;

    const alerted = this.getAlertedErrors();

    // Add new entry
    alerted[fingerprint] = {
      fingerprint,
      timestamp: Date.now(),
      session_id: sessionId
    };

    // Cleanup if too many entries
    this.cleanup(alerted);

    // Save back to sessionStorage
    this.saveAlertedErrors(alerted);
  }

  /**
   * Clear alerts when session changes
   * Removes entries from old sessions to keep storage clean
   */
  clearIfSessionChanged(): void {
    const currentSessionId = this.getCurrentSessionId();
    if (!currentSessionId) {
      // No session yet, clear all
      this.clearAll();
      return;
    }

    const alerted = this.getAlertedErrors();

    // Remove entries from old sessions
    const filtered = Object.fromEntries(
      Object.entries(alerted).filter(
        ([_, entry]) => entry.session_id === currentSessionId
      )
    );

    this.saveAlertedErrors(filtered);
  }

  /**
   * Clear all alerted errors (used on logout or session reset)
   */
  clearAll(): void {
    if (typeof sessionStorage === 'undefined') return;

    try {
      sessionStorage.removeItem(this.STORAGE_KEY);
    } catch {
      // Silent failure
    }
  }

  /**
   * Get current session ID from sessionStorage
   */
  /**
   * The current visit, from the one source of browser identity.
   *
   * This used to read `sessionStorage['sw_error_session_id']` directly -- a key
   * retired by the 2026-09-16 identity unification and written by nothing since.
   * So it returned null on every call, `hasBeenAlerted` was therefore always
   * false, `markAsAlerted` a no-op, and `clearIfSessionChanged` wiped the store
   * every time. Deduplication was silently off, and nothing failed to say so:
   * every caller reads "not yet alerted", which is the answer that looks normal.
   *
   * Read through `getSessionId()` so it cannot drift from the id again, and so a
   * browser with blocked storage gets the one stable in-memory fallback rather
   * than a fresh value per call.
   */
  private getCurrentSessionId(): string | null {
    return getSessionId() || null;
  }

  /**
   * Get all alerted errors from sessionStorage
   */
  private getAlertedErrors(): Record<string, AlertedError> {
    if (typeof sessionStorage === 'undefined') return {};

    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  }

  /**
   * Save alerted errors to sessionStorage with quota handling
   */
  private saveAlertedErrors(alerted: Record<string, AlertedError>): void {
    if (typeof sessionStorage === 'undefined') return;

    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(alerted));
    } catch (err: unknown) {
      // QuotaExceededError - clear oldest entries and retry
      if (err instanceof DOMException && err.name === 'QuotaExceededError') {
        const sorted = Object.values(alerted).sort((a, b) => a.timestamp - b.timestamp);
        const keep = sorted.slice(-50); // Keep only 50 most recent
        const reduced = Object.fromEntries(keep.map(e => [e.fingerprint, e]));

        try {
          sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(reduced));
        } catch {
          // If still failing, give up silently
        }
      }
    }
  }

  /**
   * Cleanup alerted errors to limit storage size
   * Keeps only MAX_ENTRIES most recent entries
   */
  private cleanup(alerted: Record<string, AlertedError>): void {
    const entries = Object.values(alerted);
    if (entries.length <= this.MAX_ENTRIES) return;

    // Keep only most recent MAX_ENTRIES
    const sorted = entries.sort((a, b) => b.timestamp - a.timestamp);
    const keep = sorted.slice(0, this.MAX_ENTRIES);
    const cleaned = Object.fromEntries(keep.map(e => [e.fingerprint, e]));

    // Clear and replace
    Object.keys(alerted).forEach(key => delete alerted[key]);
    Object.assign(alerted, cleaned);
  }
}

// Export singleton instance
export const errorAlertDeduplicator = new ErrorAlertDeduplicator();

// Export class for testing
export { ErrorAlertDeduplicator };
export type { AlertedError };
